import React from "react";
import { Link } from "react-router-dom";
import { LegalPage, LegalSection, LegalList } from "@/components/site/LegalPage";
import { useSiteInfo } from "@/lib/siteInfo";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";

export default function Privacy() {
  const { t } = useLang();
  useSeo({ title: t("seo.privacy.title"), description: t("seo.privacy.desc"), path: "/privacy" });
  const info = useSiteInfo() || {};
  const owner = info.company_name || "[RAGIONE SOCIALE]";
  const address = info.company_address || "[INDIRIZZO SEDE LEGALE]";
  const vat = info.company_vat || "[P.IVA / CODICE FISCALE]";
  const email = info.privacy_email || "[EMAIL PRIVACY]";

  return (
    <LegalPage
      eyebrow="Informativa"
      title="Privacy Policy"
      updated={info.policy_updated_at}
      testId="privacy-page"
    >
      <LegalSection title="1. Titolare del trattamento">
        <p>
          Il titolare del trattamento dei dati personali raccolti tramite questo sito è <strong className="text-cream">{owner}</strong>,
          con sede in {address}, P.IVA / C.F. {vat}.
        </p>
        <p>
          Per qualsiasi richiesta relativa al trattamento dei tuoi dati puoi scrivere a{" "}
          <a href={`mailto:${email}`} className="link-underline text-copperlight" data-testid="privacy-contact-email">{email}</a>.
        </p>
      </LegalSection>

      <LegalSection title="2. Quali dati raccogliamo">
        <LegalList
          items={[
            "Dati di contatto che ci comunichi volontariamente tramite il modulo contatti: nome e cognome, email, telefono, destinazione d'interesse, budget indicativo e il messaggio che scrivi.",
            "Dati della candidatura a una spedizione: nome e cognome, email, telefono e le risposte alle domande del modulo della spedizione scelta (che possono includere esperienze di viaggio, preferenze, esigenze alimentari o di salute solo se decidi di comunicarle).",
            "Dati tecnici di navigazione: indirizzo IP, tipo di browser e dispositivo, pagine visitate, registrati nei log del server per finalità di sicurezza e funzionamento.",
            "Dati di accesso all'area riservata, per i soli collaboratori autorizzati: email, password cifrata e cookie tecnici di sessione.",
          ]}
        />
        <p>
          Non raccogliamo dati di minori di 16 anni e non acquistiamo elenchi di contatti da terzi.
        </p>
      </LegalSection>

      <LegalSection title="3. Perché li usiamo e con quale base giuridica">
        <LegalList
          items={[
            "Rispondere alle tue richieste di informazioni e alle candidature, inviarti la ricevuta e ricontattarti: esecuzione di un contratto o di misure precontrattuali da te richieste (art. 6.1.b GDPR) e tuo consenso espresso tramite la casella dedicata nel modulo (art. 6.1.a GDPR).",
            "Gestire l'organizzazione del viaggio, i pagamenti e gli obblighi contabili e fiscali: obbligo legale (art. 6.1.c GDPR).",
            "Garantire la sicurezza del sito e prevenire abusi: legittimo interesse del titolare (art. 6.1.f GDPR).",
            "Statistiche di utilizzo del sito tramite strumenti di misurazione, quando attivi: solo previo tuo consenso raccolto dal banner cookie (art. 6.1.a GDPR).",
          ]}
        />
        <p>
          Il conferimento dei dati contrassegnati come obbligatori è necessario per darti una risposta: senza di essi non
          possiamo gestire la richiesta. Non effettuiamo processi decisionali automatizzati né profilazione.
        </p>
      </LegalSection>

      <LegalSection title="4. Per quanto tempo li conserviamo">
        <LegalList
          items={[
            "Richieste di contatto non seguite da una prenotazione: 24 mesi dall'ultimo contatto.",
            "Candidature e dati dei viaggiatori che hanno partecipato a una spedizione: 10 anni, per gli obblighi contabili, fiscali e assicurativi.",
            "Log tecnici del server: massimo 12 mesi.",
            "Dati usati per statistiche con consenso: massimo 14 mesi.",
          ]}
        />
      </LegalSection>

      <LegalSection title="5. A chi comunichiamo i dati">
        <p>
          I dati sono trattati dal titolare e dalle persone autorizzate che collaborano con lui. Possono essere comunicati,
          solo per quanto necessario, a:
        </p>
        <LegalList
          items={[
            "fornitori di servizi informatici (hosting del sito e del database, servizio di invio email), nominati responsabili del trattamento ai sensi dell'art. 28 GDPR;",
            "tour leader, guide e partner locali che gestiscono operativamente la spedizione a cui ti candidi;",
            "consulenti fiscali, assicurativi e legali, nei limiti degli obblighi di legge;",
            "autorità pubbliche, quando previsto da una norma.",
          ]}
        />
        <p>
          I dati non vengono venduti né ceduti a terzi per finalità di marketing. Alcuni fornitori possono trattare i dati
          in Paesi extra UE: in tal caso il trasferimento avviene sulla base di una decisione di adeguatezza della Commissione
          europea o delle Clausole Contrattuali Standard.
        </p>
      </LegalSection>

      <LegalSection title="6. I tuoi diritti">
        <p>
          Puoi esercitare in qualsiasi momento i diritti previsti dagli artt. 15-22 GDPR: accesso ai tuoi dati, rettifica,
          cancellazione, limitazione del trattamento, portabilità, opposizione al trattamento e revoca del consenso già
          prestato (senza che ciò pregiudichi la liceità dei trattamenti precedenti).
        </p>
        <p>
          Per farlo scrivi a{" "}
          <a href={`mailto:${email}`} className="link-underline text-copperlight">{email}</a>: ti risponderemo entro 30 giorni.
          Se ritieni che il trattamento violi la normativa, puoi proporre reclamo al Garante per la protezione dei dati
          personali (<span className="text-cream">www.garanteprivacy.it</span>) o rivolgerti all'autorità giudiziaria.
        </p>
      </LegalSection>

      <LegalSection title="7. Sicurezza">
        <p>
          Adottiamo misure tecniche e organizzative adeguate: connessione cifrata HTTPS, password conservate con funzioni di
          hashing non reversibili, accesso all'area riservata limitato alle persone autorizzate e distinto per ruoli,
          backup periodici e registrazione degli accessi.
        </p>
      </LegalSection>

      <LegalSection title="8. Cookie">
        <p>
          Per l'uso dei cookie e degli strumenti di misurazione consulta la{" "}
          <Link to="/cookie-policy" className="link-underline text-copperlight" data-testid="privacy-cookie-link">Cookie Policy</Link>.
        </p>
      </LegalSection>

      <LegalSection title="9. Modifiche">
        <p>
          Questa informativa può essere aggiornata per adeguarla a nuove funzionalità del sito o a modifiche normative.
          La versione in vigore è sempre quella pubblicata su questa pagina, con la data di ultimo aggiornamento indicata in alto.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
